import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBeer, faDice, faTableTennisPaddleBall } from '@fortawesome/free-solid-svg-icons';

interface DrinkingGamesProps {
  onBack: () => void;
}

type GameCategory = 'all' | 'beer-pong' | 'mexican' | 'card' | 'classic';

const DrinkingGames: React.FC<DrinkingGamesProps> = ({ onBack }) => {
  const [selectedCategory, setSelectedCategory] = useState<GameCategory>('all');
  const [expandedGame, setExpandedGame] = useState<string | null>(null);

  const categories = [
    { id: 'all' as GameCategory, name: 'All Games', color: 'bg-[#8bd3dd]' },
    { id: 'beer-pong' as GameCategory, name: 'Beer Pong', color: 'bg-[#ffd803]' },
    { id: 'mexican' as GameCategory, name: 'Mexican', color: 'bg-[#f582ae]' },
    { id: 'card' as GameCategory, name: 'Card Games', color: 'bg-[#9b5de5]' },
    { id: 'classic' as GameCategory, name: 'Classics', color: 'bg-[#b8f2e6]' },
  ];

  const games = [
    // BEER PONG
    {
      id: 'beer-pong',
      category: 'beer-pong' as GameCategory,
      name: 'Beer Pong (Official Rules)',
      icon: faTableTennisPaddleBall,
      players: '2v2 (or more)',
      difficulty: 'Medium',
      description: 'The classic beer pong with official tournament rules.',
      rules: [
        {
          title: 'Setup',
          steps: [
            'Use an 8-foot by 2-foot table (regulation size)',
            'Arrange 10 cups in pyramid formation at each end (4-3-2-1)',
            'Fill cups with 2-3 oz of beer each',
            'Place cups at the edge of the table',
            'Each team gets 2 ping pong balls',
          ]
        },
        {
          title: 'Gameplay',
          steps: [
            'Teams take turns throwing balls (each player throws once per turn)',
            'If a ball lands in a cup, remove it and the opposing team drinks it',
            'Elbow must stay behind the table edge when throwing',
            'Balls can bounce (but can be swatted away if bounced)',
            'Request ONE re-rack per game to rearrange remaining cups',
          ]
        },
        {
          title: 'Special Rules',
          steps: [
            'Both balls in: If both players make their shots, they get balls back',
            'Redemption: When one team wins, losers get one last chance to tie',
            'Island/Lonely Cup: Call out an isolated cup for bonus shot',
            'Rollbacks: If both make it on the same turn, get balls back',
          ]
        },
        {
          title: 'Winning',
          steps: [
            'First team to eliminate all opponent cups wins',
            'Losers must drink all remaining cups on their side',
          ]
        }
      ]
    },

    // CANDELABRO
    {
      id: 'candelabro',
      category: 'mexican' as GameCategory,
      name: 'Candelabro (Chandelier)',
      icon: faBeer,
      players: '4-10',
      difficulty: 'Hard',
      description: 'Mexican drinking game where you try to hit cups with a ping pong ball. When the sacred drink is hit, chaos ensues!',
      rules: [
        {
          title: 'Setup',
          steps: [
            'Place 2 cups in the center: one upside down, one on top filled with a mix of all drinks (THE SACRED DRINK)',
            'Each player places their drink around the center cups in a circle',
            'Get a ping pong ball',
            'Stand around the table',
          ]
        },
        {
          title: 'How to Play',
          steps: [
            'Players take turns bouncing/throwing the ball',
            'If you hit someone\'s cup, they drink',
            'If you hit the SACRED DRINK in the center, ALL players must:',
            '  1. Chug their entire drink',
            '  2. Place cup on table edge',
            '  3. Flip the cup to land upside down (one-handed flip)',
            'Last person to flip their cup successfully drinks the SACRED DRINK',
          ]
        },
        {
          title: 'Pro Tips',
          steps: [
            'Bounce shots are harder to block but more likely to hit',
            'Aim for enemies, not friends',
            'Practice your cup flipping beforehand',
            'The sacred drink gets NASTY - don\'t be last!',
          ]
        }
      ]
    },

    // MEXICO DICE
    {
      id: 'mexico',
      category: 'mexican' as GameCategory,
      name: 'Mexico (Dice Game)',
      icon: faDice,
      players: '3-8',
      difficulty: 'Easy',
      description: 'A Mexican dice bluffing game where you roll, lie, and drink.',
      rules: [
        {
          title: 'Setup',
          steps: [
            'Need: 2 dice and a cup to hide them',
            'Sit in a circle with drinks',
            'Each player starts with 3 lives',
          ]
        },
        {
          title: 'Dice Values (Highest to Lowest)',
          steps: [
            '21 (2+1) = Mexico (HIGHEST)',
            'Doubles: 66, 55, 44, 33, 22, 11',
            'Regular: 65, 64, 63, 62, 61, 54, 53, etc.',
            'Note: Higher number goes first (65 not 56)',
          ]
        },
        {
          title: 'How to Play',
          steps: [
            'Roll dice under cup, peek at your roll (don\'t show others)',
            'Announce your roll (you can LIE)',
            'Next player can:',
            '  - BELIEVE: Accept and must roll HIGHER or lie better',
            '  - CHALLENGE: Lift cup to check',
            'If challenged and caught lying: You drink and lose a life',
            'If challenged but told truth: Challenger drinks and loses a life',
          ]
        },
        {
          title: 'Winning',
          steps: [
            'Last player with lives remaining wins',
            'Rolling Mexico (21) = instant win for that round',
          ]
        }
      ]
    },

    // VEINTIUNO (21)
    {
      id: 'veintiuno',
      category: 'mexican' as GameCategory,
      name: 'Veintiuno (21)',
      players: '3-10',
      difficulty: 'Easy',
      description: 'Count to 21 with special rules. Mess up? Drink!',
      rules: [
        {
          title: 'Basic Rules',
          steps: [
            'Sit in a circle',
            'Count from 1 to 21, one number per person',
            'Whoever says 21 makes a new rule and drinking starts over',
          ]
        },
        {
          title: 'Example Rules (Add These When You Hit 21)',
          steps: [
            'Replace "7" with a clap',
            'Say all multiples of 3 backwards',
            'Stand up for even numbers',
            'Replace "15" with "fuck"',
            'Skip saying 11, point at someone instead',
            'Swap direction at every multiple of 5',
          ]
        },
        {
          title: 'What Happens When You Mess Up',
          steps: [
            'Break a rule? DRINK',
            'Say wrong number? DRINK',
            'Hesitate too long? DRINK',
            'Start a new round with all rules still active',
          ]
        }
      ]
    },

    // KINGS CUP
    {
      id: 'kings',
      category: 'card' as GameCategory,
      name: 'Kings Cup (Ring of Fire)',
      players: '3-12',
      difficulty: 'Medium',
      description: 'Classic card drinking game with a cup in the middle.',
      rules: [
        {
          title: 'Setup',
          steps: [
            'Place empty cup in center of table',
            'Spread deck of cards face-down around cup in a circle',
            'Players sit around table with drinks',
          ]
        },
        {
          title: 'Card Rules',
          steps: [
            '2 - You: Pick someone to drink',
            '3 - Me: You drink',
            '4 - Floor: Last to touch floor drinks',
            '5 - Guys: All guys drink',
            '6 - Chicks: All girls drink',
            '7 - Heaven: Last to point up drinks',
            '8 - Mate: Pick a drinking buddy',
            '9 - Rhyme: Say a word, others rhyme. First to fail drinks',
            '10 - Categories: Name category, go around. First to fail drinks',
            'Jack - Make a Rule: Create any rule',
            'Queen - Questions: Ask questions until someone answers or fails',
            'King - Pour: Pour some of your drink into center cup',
            'Ace - Waterfall: Everyone drinks, can\'t stop until person to your right stops',
          ]
        },
        {
          title: 'Important',
          steps: [
            'Whoever draws 4th King drinks the center cup (game ends)',
            'Break the circle of cards = drink',
          ]
        }
      ]
    },

    // FLIP CUP
    {
      id: 'flip-cup',
      category: 'classic' as GameCategory,
      name: 'Flip Cup',
      players: '6+ (teams)',
      difficulty: 'Easy',
      description: 'Fast-paced team relay race. Drink and flip!',
      rules: [
        {
          title: 'Setup',
          steps: [
            'Divide into 2 equal teams',
            'Teams stand on opposite sides of table',
            'Each player gets a cup with 2-3 oz beer',
            'Cups at edge of table',
          ]
        },
        {
          title: 'How to Play',
          steps: [
            'First players toast and drink their beer',
            'Place empty cup upside down on table edge',
            'Use ONE FINGER to flip cup to land upside down',
            'Once landed, next teammate goes',
            'Continue relay down the line',
          ]
        },
        {
          title: 'Winning',
          steps: [
            'First team to have all players successfully flip wins',
            'Losers finish their drinks',
          ]
        }
      ]
    },

    // POWER HOUR
    {
      id: 'power-hour',
      category: 'classic' as GameCategory,
      name: 'Power Hour',
      players: '2+',
      difficulty: 'Hard',
      description: 'Take a shot of beer every minute for an hour. Sounds easy? It\'s not.',
      rules: [
        {
          title: 'The Challenge',
          steps: [
            'Set a timer to beep every 60 seconds',
            'Every beep, everyone takes a 1-1.5 oz shot of beer',
            'That\'s 60 shots in 60 minutes = ~5 beers',
            'Can\'t skip a round',
          ]
        },
        {
          title: 'Variations',
          steps: [
            'Century Club: 100 shots in 100 minutes (7+ beers)',
            'Power Half-Hour: 30 minutes for beginners',
            'Change songs every minute (make a playlist)',
          ]
        },
        {
          title: 'Pro Tips',
          steps: [
            'Pace yourself, it adds up FAST',
            'Eat before starting',
            'Have water between rounds',
            'Bathroom breaks don\'t pause the clock',
          ]
        }
      ]
    },

    // QUARTERS
    {
      id: 'quarters',
      category: 'classic' as GameCategory,
      name: 'Quarters',
      players: '2-8',
      difficulty: 'Medium',
      description: 'Bounce a quarter into a glass. Miss? Drink.',
      rules: [
        {
          title: 'Setup',
          steps: [
            'Get a quarter and a shot glass',
            'Players sit around table',
            'Everyone has a drink',
          ]
        },
        {
          title: 'How to Play',
          steps: [
            'Bounce quarter off table trying to land it in shot glass',
            'Make it: Pick someone to drink',
            'Miss it: Pass quarter to next player',
            'Get 3 in a row: Make a rule',
          ]
        },
        {
          title: 'House Rules (Optional)',
          steps: [
            'Interference: Block bounce with your hand if you\'re fast',
            'Speed Quarters: 2 quarters going around at once',
            'Dare Quarters: Make a dare instead of drinking',
          ]
        }
      ]
    },

    // RIDE THE BUS
    {
      id: 'ride-the-bus',
      category: 'card' as GameCategory,
      name: 'Ride the Bus',
      players: '4-10',
      difficulty: 'Hard',
      description: 'Card game where one unlucky person "rides the bus" at the end.',
      rules: [
        {
          title: 'Round 1: Red or Black',
          steps: [
            'Guess if your card is red or black',
            'Wrong? Drink',
          ]
        },
        {
          title: 'Round 2: Higher or Lower',
          steps: [
            'Guess if next card is higher or lower than first',
            'Wrong? Drink 2',
          ]
        },
        {
          title: 'Round 3: In Between or Outside',
          steps: [
            'Guess if next card is between or outside your first 2 cards',
            'Wrong? Drink 3',
          ]
        },
        {
          title: 'Round 4: Suit',
          steps: [
            'Guess the suit of next card',
            'Wrong? Drink 4',
          ]
        },
        {
          title: 'Round 5: Ride the Bus',
          steps: [
            'Person with worst hand "rides the bus"',
            'Layout cards face down in pyramid',
            'Flip cards one by one',
            'Face card? Drink and start over from bottom',
            'Must get through entire pyramid to win',
          ]
        }
      ]
    },

    // NEVER HAVE I EVER
    {
      id: 'never-have-i-ever',
      category: 'classic' as GameCategory,
      name: 'Never Have I Ever',
      players: '3+',
      difficulty: 'Easy',
      description: 'Learn secrets about your friends while getting drunk.',
      rules: [
        {
          title: 'How to Play',
          steps: [
            'Sit in circle with drinks',
            'Each player holds up 5 or 10 fingers',
            'Take turns saying "Never have I ever [done something]"',
            'If you HAVE done it: Put a finger down and drink',
            'First person to put all fingers down loses',
          ]
        },
        {
          title: 'Tips for Good Statements',
          steps: [
            'Target specific people with inside knowledge',
            'Don\'t be too tame - spice it up',
            'Don\'t be too wild - keep it fun not traumatic',
            'Examples: "Never have I ever kissed someone in this room"',
          ]
        }
      ]
    },

    // BUFFALO
    {
      id: 'buffalo',
      category: 'classic' as GameCategory,
      name: 'Buffalo',
      players: '2+ (ongoing)',
      difficulty: 'Easy',
      description: 'A lifelong drinking game. Once you\'re in, you\'re ALWAYS in.',
      rules: [
        {
          title: 'The Only Rule',
          steps: [
            'You must ALWAYS drink with your non-dominant hand',
            'Right-handed? Drink with left',
            'Left-handed? Drink with right',
          ]
        },
        {
          title: 'The Catch',
          steps: [
            'If someone catches you drinking with dominant hand, yell "BUFFALO!"',
            'You must chug the rest of your drink',
            'Game never ends once you join',
            'Applies to ALL drinks (water, coffee, etc.) forever',
          ]
        },
        {
          title: 'Warning',
          steps: [
            'This game is for LIFE',
            'Your friends will catch you months/years later',
            'Consider carefully before playing',
          ]
        }
      ]
    },
  ];

  const filteredGames = selectedCategory === 'all'
    ? games
    : games.filter(game => game.category === selectedCategory);

  const toggleGame = (gameId: string) => {
    setExpandedGame(expandedGame === gameId ? null : gameId);
  };

  return (
    <div className="min-h-screen p-8 lg:ml-64 lg:mr-80">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-[#8bd3dd] border-4 border-black p-6 mb-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <button
            onClick={onBack}
            className="mb-4 bg-black text-[#8bd3dd] px-4 py-2 border-4 border-black hover:bg-[#f582ae] hover:text-black transition-colors font-bold"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back to Activities
          </button>
          <h1 className="text-4xl font-bold text-black flex items-center gap-3">
            <FontAwesomeIcon icon={faBeer} />
            Drinking Games
          </h1>
          <p className="text-black mt-2 text-lg">
            {games.length} games to get you absolutely wrecked
          </p>
        </div>

        {/* Warning */}
        <div className="bg-[#f582ae] border-4 border-black p-6 mb-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="text-2xl font-bold text-black mb-2">⚠️ Drink Responsibly</h2>
          <p className="text-black">
            These games are for entertainment. Know your limits, stay hydrated, never drink and drive, and look out for your friends.
          </p>
        </div>

        {/* Category Filter */}
        <div className="bg-white border-4 border-black p-6 mb-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="text-2xl font-bold text-black mb-4">Filter by Category</h2>
          <div className="flex flex-wrap gap-3">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`${category.color} border-4 border-black px-6 py-3 font-bold transition-all ${
                  selectedCategory === category.id
                    ? 'shadow-none translate-x-1 translate-y-1'
                    : 'shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Games List */}
        <div className="space-y-4">
          {filteredGames.map(game => (
            <div
              key={game.id}
              className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
            >
              {/* Game Header */}
              <button
                onClick={() => toggleGame(game.id)}
                className="w-full p-6 text-left hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-black mb-2 flex items-center gap-3">
                      <FontAwesomeIcon icon={game.icon || faBeer} className="text-black" />
                      {game.name}
                    </h3>
                    <p className="text-black mb-3">{game.description}</p>
                    <div className="flex gap-4 text-sm">
                      <span className="bg-black text-white px-3 py-1 font-bold">
                        👥 {game.players}
                      </span>
                      <span className="bg-black text-white px-3 py-1 font-bold">
                        📊 {game.difficulty}
                      </span>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-black ml-4">
                    {expandedGame === game.id ? '−' : '+'}
                  </div>
                </div>
              </button>

              {/* Game Rules (Expanded) */}
              {expandedGame === game.id && (
                <div className="border-t-4 border-black p-6 bg-gray-50">
                  {game.rules.map((section, idx) => (
                    <div key={idx} className="mb-6 last:mb-0">
                      <h4 className="text-xl font-bold text-black mb-3 bg-[#ffd803] inline-block px-4 py-2 border-4 border-black">
                        {section.title}
                      </h4>
                      <ul className="space-y-2 mt-3">
                        {section.steps.map((step, stepIdx) => (
                          <li key={stepIdx} className="text-black flex items-start">
                            <span className="mr-3 font-bold text-[#f582ae]">•</span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredGames.length === 0 && (
          <div className="bg-white border-4 border-black p-12 text-center shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-2xl font-bold text-black">No games found in this category</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrinkingGames;
